<template>
  <div class="parcel-list-selector">
    <div class="parcel-list-controls">
      <button
        v-if="showToggle"
        class="parcel-toggle-button"
        type="button"
        @click="$emit('toggle-view')"
      >
        <img src="@/assets/images/icons/map_layers.svg" alt="Map" />
      </button>
      <input
        v-model="searchText"
        type="text"
        class="parcel-search-input"
        :placeholder="$t('common.search')"
      />
    </div>
    <div class="">
      <DxSelectBox
        v-model="seasonUuid"
        :data-source="seasonOptions"
        display-expr="name"
        value-expr="uuid"
        :show-clear-button="true"
        placeholder=""
      />
    </div>

    <div ref="list" class="parcel-list">
      <div
        v-for="(layer, index) in filteredLayers"
        :key="layer.uuid"
        class="parcel-list-item"
        :class="{ selected: isSelected(layer) }"
        @click="toggleLayer(layer)"
      >
        <div class="parcel-list-item-header">
          <div class="parcel-name">{{ layer.name || '-' }}</div>
          <input type="checkbox" class="parcel-checkbox" :checked="isSelected(layer)" readonly />
        </div>
        <div class="parcel-meta">
          <div class="parcel-meta-row">
            <span class="parcel-meta-label">{{ $t('common.culture') }}</span>
            <span class="parcel-meta-value">{{ getCultureName(layer) }}</span>
          </div>
          <div class="parcel-meta-row">
            <span class="parcel-meta-label">{{ $t('common.surface') }}</span>
            <span class="parcel-meta-value">{{ formatSurface(layer.surface) }}</span>
          </div>
          <div class="parcel-meta-row">
            <span class="parcel-meta-label">{{ $t('common.season') }}</span>
            <span class="parcel-meta-value">{{ getSeasonName(layer) }}</span>
          </div>
          <div class="parcel-meta-row">
            <span class="parcel-meta-label">{{ $t('common.unit') }}</span>
            <span class="parcel-meta-value">{{ getUnitName(layer) }}</span>
          </div>
        </div>
      </div>
      <div v-if="filteredLayers.length === 0" class="parcel-empty">
        {{ $t('common.no_data_found') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { DxSelectBox } from "devextreme-vue/select-box";

const getBiggestSeasonUuid = (options) => {
  if (!Array.isArray(options) || options.length === 0) return "";

  const validYearSeasons = options.filter((season) => {
    if (!season || !season.uuid) return false;
    const year = Number(season.year);
    return Number.isFinite(year) && year > 0;
  });

  if (validYearSeasons.length > 0) {
    const biggestYearSeason = validYearSeasons.reduce((currentMax, season) => {
      return Number(season.year) > Number(currentMax.year) ? season : currentMax;
    }, validYearSeasons[0]);

    return biggestYearSeason.uuid;
  }

  return options.find((season) => season && season.uuid)?.uuid || "";
};

export default {
  name: "ParcelListSelector",
  components: {
    DxSelectBox,
  },
  props: {
    seasons: {
      type: Array,
      default: () => [],
    },
    selectedLayerUuids: {
      type: Array,
      default: () => [],
    },
    defaultSeasonUuid: {
      type: String,
      default: "",
    },
    showToggle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const options = Array.isArray(this.seasons) ? this.seasons : [];
    const biggestSeasonUuid = getBiggestSeasonUuid(options);

    return {
      searchText: "",
      seasonUuid: this.defaultSeasonUuid || biggestSeasonUuid,
    };
  },
  computed: {
    ...mapGetters({
      storeLayers: "layers/layers",
    }),
    seasonOptions() {
      return Array.isArray(this.seasons) ? this.seasons : [];
    },
    effectiveLayers() {
      return this.storeLayers;
    },
    filteredLayers() {
      const text = this.searchText.trim().toLowerCase();
      return (Array.isArray(this.effectiveLayers) ? this.effectiveLayers : [])
        .filter((layer) => this.matchesSeason(layer))
        .filter((layer) => {
          if (!text) return true;
          const haystack = [
            layer.name,
            this.getCultureName(layer),
            this.formatSurface(layer.surface),
            this.getSeasonName(layer),
            this.getUnitName(layer),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(text);
        });
    },
  },
  watch: {
    defaultSeasonUuid(newValue) {
      if (newValue && this.seasonUuid !== newValue && this.seasonOptions.some((season) => season.uuid === newValue)) {
        this.seasonUuid = newValue;
        return;
      }

      const options = Array.isArray(this.seasonOptions) ? this.seasonOptions : [];
      if (!newValue && !this.seasonUuid && options.length > 0) {
        this.seasonUuid = getBiggestSeasonUuid(options);
      }
    },
    seasonOptions(newOptions) {
      const options = Array.isArray(newOptions) ? newOptions : [];
      const hasSeason = this.seasonUuid && options.some((season) => season.uuid === this.seasonUuid);
      if (!hasSeason) {
        if (this.defaultSeasonUuid) {
          this.seasonUuid = options.some((season) => season.uuid === this.defaultSeasonUuid) ? this.defaultSeasonUuid : "";
        } else {
          this.seasonUuid = getBiggestSeasonUuid(options);
        }
      }
      this.$emit("reset-selection");
    },
    seasonUuid(newValue) {
      this.$emit("season-change", newValue);
      this.fetchLayersForSeason(newValue);
    },
    filteredLayers() {
      this.resetVirtualScroll();
    },
  },
  mounted() {
    this.fetchLayersForSeason(this.seasonUuid);
  },
  methods: {
    ...mapActions({
      getLayers: "layers/getLayers",
    }),
    resetVirtualScroll() {
      if (this.$refs.list) {
        this.$refs.list.scrollTop = 0;
      }
    },
    fetchLayersForSeason(seasonUuid) {
      this.getLayers({
        season_uuid: seasonUuid || null,
        enablePagination: 0,
        pagination: 0,
        limit: 9999,
      }).catch(() => {});
    },
    toggleLayer(layer) {
      this.$emit("toggle-layer", layer);
    },
    isSelected(layer) {
      return Array.isArray(this.selectedLayerUuids) && this.selectedLayerUuids.includes(layer.uuid);
    },
    matchesSeason(layer) {
      if (!this.seasonUuid) return true;
      const layerSeasonUuid = layer.season_uuid || layer.season?.uuid || "";
      if (layerSeasonUuid) {
        return layerSeasonUuid === this.seasonUuid;
      }
      const selectedSeason = this.seasonOptions.find((season) => season.uuid === this.seasonUuid);
      if (!selectedSeason) return true;
      const seasonName = this.getSeasonName(layer);
      return seasonName === selectedSeason.name;
    },
    getCultureName(layer) {
      return layer.culture?.name || layer.culture_name || "-";
    },
    getSeasonName(layer) {
      return layer.season_name || layer.season?.name || layer.season || "-";
    },
    getUnitName(layer) {
      return layer.department_name || layer.department?.name || "-";
    },
    formatSurface(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      const num = Number(value);
      if (Number.isNaN(num)) {
        return String(value);
      }
      return `${num.toFixed(2)} ha`;
    },
  },
};
</script>

<style scoped>
.parcel-list-selector {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 5px;
  padding-right: 5px;
}

.parcel-list-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.parcel-search-input {
  flex: 1 1 160px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  padding: 0 10px;
  font-size: 14px;
}

.parcel-list-controls .dx-selectbox {
  min-width: 160px;
  flex: 0 0 auto;
}

.parcel-toggle-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #d9d9d9;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.parcel-toggle-button img {
  width: 18px;
  height: 18px;
}

.parcel-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: calc(100vh - 260px);
  overflow-y: auto;
  position: relative;
}

.parcel-list-spacer {
  width: 100%;
}

.parcel-list-item {
  background: #ffffff;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 120px;
  box-sizing: border-box;
}

.parcel-list-item.selected {
  border-color: #1a8f3d;
  box-shadow: 0 0 0 1px #1a8f3d inset;
}

.parcel-list-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.parcel-name {
  font-weight: 700;
  font-size: 14px;
  color: #323232;
}

.parcel-checkbox {
  width: 18px;
  height: 18px;
}

.parcel-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
}

.parcel-meta-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.parcel-meta-label {
  font-size: 11px;
  color: #797979;
  text-transform: uppercase;
}

.parcel-meta-value {
  font-size: 13px;
  color: #565656;
}

.parcel-empty {
  text-align: center;
  color: #797979;
  font-size: 13px;
}
</style>
